import { SignInButton } from "@clerk/clerk-react";
import ButtonCart from "components/cart/ButtonCart";
import { useUser } from "components/clerk";
import { SearchBar } from "components/navigation";
import { Box, Button, HStack, Icon, Paragraph } from "components/ui";
import NotificationBadge from "components/ui/NotificationBadge";
import { router } from "expo-router";
import { Bell, ShoppingCart, UsersRound } from "lucide-react-native";
import React, { useEffect, useRef } from "react";
import { Pressable } from "react-native";
import { useGetNotificationQuery } from "store/abcoApi";
import { useSiteSearch } from "utils/hooks/useSiteSearch";
import Container from "../Container";
import HeaderSearchResults from "./HeaderSearchResults";
import { Toast } from "react-native-toast-notifications";
import { useDispatch } from "react-redux";

import { emptySplitApi } from "store/baseApi";
import { eventTypeLabels } from "types/constants.type";

type Props = {};

const NotificationBadgeContainer = (props: any) => {
	const { isSignedIn } = useUser();
	const dispatch = useDispatch();

	const {
		data: notificationData,
		isLoading,
		isFetching,
		isError,
		error,
		currentData,
	} = useGetNotificationQuery(
		{
			pageSize: 1,
			pageNumber: 1,
			hasRead: false,
		},
		{
			skip: !isSignedIn,
			pollingInterval: 30000, // 30 seconds
		},
	);

	const previousCountRef = useRef<number>(0);
	const firstLoadCompleteRef = useRef<boolean>(false);

	useEffect(() => {
		// Wait until we have valid data
		if (!notificationData?.metaData) return;

		const currentCount = notificationData.metaData.totalItemCount ?? 0;

		// Don't trigger on first valid data load
		if (!firstLoadCompleteRef.current) {
			previousCountRef.current = currentCount;
			firstLoadCompleteRef.current = true;
			return;
		}

		const previousCount = previousCountRef.current;

		if (previousCount !== currentCount && notificationData.list?.length > 0) {
			dispatch(emptySplitApi.util.invalidateTags(["Notification"]));

			if (currentCount > previousCount) {
				const notification = notificationData.list[0];
				if (!notification) return;

				let message;

				switch (notification.eventType) {
					case "CartWaitingForApproval":
						message = `Cart ${notification.transactionNumber} for ${notification.siteName} is waiting for approval`;
						break;
					case "CartApproved":
						message = `Cart ${notification.transactionNumber} for ${notification.siteName} has been approved`;
						break;
					case "CartRejected":
						message = `Cart ${notification.transactionNumber} for ${notification.siteName} has been rejected`;
						break;
					case "ShippingInTransit":
						message = `${notification.transactionNumber} for ${notification.siteName} is in transit`;
						break;
					case "ShippingInProgress":
						message = `${notification.transactionNumber} for ${notification.siteName} is in progress`;
						break;
					case "ShippingCompleted":
						message = `${notification.transactionNumber} for ${notification.siteName} has been completed`;
						break;
					case "ShippingFailure":
						message = `${notification.transactionNumber} for ${notification.siteName} has failed`;
						break;
				}

				if (message) {
					Toast.show(message, {
						type: "info",
						textStyle: { fontSize: 20 },
						placement: "bottom",
					});
				}
			}
		}

		previousCountRef.current = currentCount;
	}, [notificationData?.metaData?.totalItemCount]);

	return (
		<Box position="absolute" top={-8} right={-8}>
			{notificationData != null && notificationData.metaData.totalItemCount >= 1 && <NotificationBadge notificationCount={notificationData.metaData.totalItemCount} notificationColor="danger" />}
		</Box>
	);
};

const HeaderSecondary = (props: Props) => {
	const { isOpen: siteSearchOpen, searchResults, searchTerm, close: closeSiteSearch } = useSiteSearch();
	const { isSignedIn } = useUser();

	const accountNavigationCollection = [
		{
			title: null,
			href: "/notifications",
			icon: Bell,
			notification: {
				count: 3,
				color: "danger",
			},
		},
		{ title: null, href: "/profile", icon: UsersRound },
		{
			title: "Cart",
			icon: ShoppingCart,
		},
	];

	return (
		<Container
			noPadding
			// sx={{
			// 	"@md": { width: "100%", paddingLeft: 10, paddingRight: 10, display: "flex" },
			// 	"@lg": { width: "100%", paddingLeft: 15, paddingRight: 15 },
			// 	"@xl": { width: "$containerWidthXL" },
			// }}
			// display="none"
			// marginLeft="auto"
			// marginRight="auto"
		>
			<Box position="relative" zIndex={99} backgroundColor="$primarydarkblue" paddingHorizontal={28} paddingVertical={24} borderBottomLeftRadius={siteSearchOpen ? 0 : 8} borderBottomRightRadius={siteSearchOpen ? 0 : 8}>
				<HStack space="xl" mx="$0.5">
					<SearchBar />

					<HStack space="lg" mx="$0.5">
						{!isSignedIn && !siteSearchOpen && (
							<Box flexDirection="row" gap={8}>
								<ButtonCart />
								<Button>
									<SignInButton fallbackRedirectUrl={"https://abcopro.co.nz"} forceRedirectUrl={"https://abcopro.co.nz"}>
										<Box
											//borderWidth={1}
											//borderColor={hovered ? "$basicwhite" : "transparent"}
											// @ts-ignore
											style={{ transition: "all 0.3s ease", cursor: "pointer" }}
											flexDirection="row"
											alignItems="center"
											h={"$full"}
											$hover-backgroundColor="$primarydark"
											borderRadius={12}
											px="$4"
											backgroundColor="$primarylight"
										>
											<Icon as={UsersRound} color="$basicwhite " mr={"$2"} />
											<Paragraph sx={{ alignItems: "center", color: "$basicwhite" }}>Login / Sign up</Paragraph>
										</Box>
									</SignInButton>
								</Button>
							</Box>
						)}

						{isSignedIn && !siteSearchOpen && (
							<Box flexDirection="row" gap={8}>
								{accountNavigationCollection.map((props: any, key: any) => {
									const { title, icon, notification } = props;

									if (title === "Cart") {
										return <ButtonCart key={key} />;
									}
									return (
										<Button isLink key={key} href={props.href} justifyContent="center" $hover-backgroundColor="$primarydark" position="relative" borderRadius={12} px="$4" backgroundColor="$primarylight" flex={1}>
											{notification && <NotificationBadgeContainer />}

											<Icon size={title ? "md" : "xl"} as={icon} color="$basicwhite " mr={title ? "$2" : "$0"} />
											<Paragraph sx={{ alignItems: "center", color: "$basicwhite" }}>{title}</Paragraph>
										</Button>
									);
								})}
							</Box>
						)}

						{siteSearchOpen && (
							<Box flexDirection="row" gap={8}>
								<Button
									onPress={() => {
										router.navigate("/search?term=" + searchTerm);
										closeSiteSearch();
									}}
									justifyContent="center"
									$hover-backgroundColor="$primarydark"
									borderRadius="$2xl"
									px="$4"
									backgroundColor="$secondarydark"
									height={60}
									flex={1}
								>
									<Paragraph sx={{ alignItems: "center", color: "$basicwhite" }}>Search</Paragraph>
								</Button>
								<ButtonCart closeSearch />
							</Box>
						)}
					</HStack>
				</HStack>
			</Box>

			{siteSearchOpen && (
				<Pressable
					// @ts-ignore
					style={{
						position: "fixed",
						width: "100%",
						left: 0,
						top: 0,
						bottom: 0,
						height: "100vh",
						zIndex: -1,
						backgroundColor: "rgba(0, 0, 0, 0.5)",
						cursor: "auto",
					}}
					onPress={closeSiteSearch}
				></Pressable>
			)}
			<Box bg="white" borderRadius={12} w="100%">
				<HeaderSearchResults />
			</Box>
		</Container>
	);
};

export default HeaderSecondary;
